import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2, Music, CheckCircle, AlertCircle, Settings, Trash2, Layers, FileText } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { songsApi } from '../../api/songs';
import { SongUpload } from './SongUpload';
import { UserManagement } from './UserManagement';
import { Settings as SettingsModal } from '../Settings/Settings';
import type { SongSummary, ScanResult } from '../../types';

export function AdminPage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [songs, setSongs] = useState<SongSummary[]>([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showClearLibraryModal, setShowClearLibraryModal] = useState(false);
  const [clearingLibrary, setClearingLibrary] = useState(false);

  useEffect(() => {
    // Redirect if not admin
    if (user && !user.isAdmin) {
      navigate('/');
      return;
    }
    loadSongs();
  }, [user, navigate]);

  const loadSongs = async () => {
    setLoading(true);
    try {
      const data = await songsApi.getAllSongs();
      setSongs(data);
    } catch (error) {
      console.error('Failed to load songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    setScanError(null);

    try {
      const result = await songsApi.scanMedia();
      setScanResult(result);
      await loadSongs(); // Reload songs after scan
    } catch (error) {
      setScanError((error as { message?: string })?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteSong = async (id: number) => {
    setDeletingId(id);
    try {
      await songsApi.deleteSong(id);
      await loadSongs(); // Reload songs after deletion
    } catch (error) {
      console.error('Failed to delete song:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleClearLibrary = async () => {
    setClearingLibrary(true);
    try {
      await songsApi.deleteAllSongs();
      setShowClearLibraryModal(false);
      await loadSongs(); // Reload songs after deletion
      setScanResult(null); // Clear scan results
    } catch (error) {
      console.error('Failed to clear library:', error);
    } finally {
      setClearingLibrary(false);
    }
  };

  return (
    <div className="min-h-screen bg-monster-dark pb-8">
      {/* Header */}
      <header className="bg-monster-card border-b border-monster-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
            >
              <ArrowLeft size={28} />
            </button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-monster-blue to-monster-crimson bg-clip-text text-transparent">
              Admin Panel
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 hidden sm:block">
              <span className="text-white font-medium">{user?.username}</span>
              <span className="ml-2 text-xs bg-monster-blue px-2 py-1 rounded">Admin</span>
            </span>
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-12 h-12 flex items-center justify-center rounded-full hover:bg-monster-dark transition-colors"
              title="Settings"
            >
              <Settings size={28} />
            </button>
            <button onClick={handleLogout} className="btn-secondary text-sm">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Upload, Subsets & Users */}
          <div className="space-y-6">
            <SongUpload />

            {/* Manage Subsets */}
            <div className="card">
              <h2 className="text-2xl font-bold mb-4">Subset Management</h2>
              <p className="text-sm text-gray-400 mb-4">
                Create and manage song subsets for competitions and voting rounds.
                Control visibility and organize your song library.
              </p>
              <button
                onClick={() => navigate('/admin/subsets')}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                <Layers size={20} />
                Manage Subsets
              </button>
            </div>

            {/* Welcome Page */}
            <div className="card">
              <h2 className="text-2xl font-bold mb-4">Welcome Page</h2>
              <p className="text-sm text-gray-400 mb-4">
                Customize the welcome message that users see on first login or after updates.
                Supports markdown and can embed songs from your library.
              </p>
              <button
                onClick={() => navigate('/admin/welcome')}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                <FileText size={20} />
                Edit Welcome Page
              </button>
            </div>

            {/* User Management */}
            <UserManagement />
          </div>

          {/* Right Column: Scan & Stats */}
          <div className="space-y-6">
            {/* Scan Media Folder */}
            <div className="card">
              <h2 className="text-2xl font-bold mb-4">Scan Media Folder</h2>
              <p className="text-sm text-gray-400 mb-4">
                Scan the media folder to detect and update songs in the database.
                This will read MP3 metadata and markdown files.
              </p>

              <button
                onClick={handleScan}
                disabled={scanning}
                className="w-full btn-primary"
              >
                {scanning ? (
                  <>
                    <Loader2 className="inline animate-spin mr-2" size={20} />
                    Scanning...
                  </>
                ) : (
                  <>
                    <RefreshCw className="inline mr-2" size={20} />
                    Scan Media Folder
                  </>
                )}
              </button>

              {scanError && (
                <div className="mt-4 p-4 bg-monster-crimson/10 border border-monster-crimson/30 rounded">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-monster-crimson flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-semibold text-monster-crimson mb-1">Scan Failed</p>
                      <p className="text-sm text-gray-300">{scanError}</p>
                    </div>
                  </div>
                </div>
              )}

              {scanResult && (
                <div className="mt-4 space-y-3">
                  {/* Success Summary */}
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <p className="font-semibold text-green-500 mb-2">
                          Scan Complete: {scanResult.total} song{scanResult.total !== 1 ? 's' : ''} processed
                        </p>
                        <div className="text-sm text-gray-300 space-y-1">
                          {scanResult.added.length > 0 && (
                            <p>✓ Added {scanResult.added.length}: {scanResult.added.join(', ')}</p>
                          )}
                          {scanResult.updated.length > 0 && (
                            <p>↻ Updated {scanResult.updated.length}: {scanResult.updated.join(', ')}</p>
                          )}
                          {scanResult.added.length === 0 && scanResult.updated.length === 0 && scanResult.total === 0 && (
                            <p>No songs found in media folder</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Errors */}
                  {scanResult.errors.length > 0 && (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
                        <div className="flex-1">
                          <p className="font-semibold text-yellow-500 mb-2">
                            {scanResult.errors.length} error{scanResult.errors.length !== 1 ? 's' : ''}
                          </p>
                          <ul className="text-sm text-gray-300 space-y-1">
                            {scanResult.errors.map((err, idx) => (
                              <li key={idx}>✗ {err.file}: {err.error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Song Library Stats */}
            <div className="card">
              <h2 className="text-2xl font-bold mb-4">Library Statistics</h2>

              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-monster-blue mx-auto" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-monster-dark rounded">
                    <div className="flex items-center gap-2">
                      <Music className="text-monster-blue" size={20} />
                      <span className="text-gray-400">Total Songs</span>
                    </div>
                    <span className="text-2xl font-bold">{songs.length}</span>
                  </div>

                  {songs.length > 0 && (
                    <>
                      <div className="flex items-center justify-between p-3 bg-monster-dark rounded">
                        <span className="text-gray-400 text-sm">Total Duration</span>
                        <span className="font-semibold">
                          {Math.round(
                            songs.reduce((acc, s) => acc + (s.durationSeconds || 0), 0) / 60
                          )} minutes
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-monster-dark rounded">
                        <span className="text-gray-400 text-sm">Genres</span>
                        <span className="font-semibold">
                          {new Set(songs.map(s => s.genre).filter(Boolean)).size}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Recent Songs */}
            {songs.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Recent Songs</h2>
                  <button
                    onClick={() => setShowClearLibraryModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-monster-crimson/10 hover:bg-monster-crimson/20 text-monster-crimson rounded text-sm font-medium transition-colors"
                  >
                    <Trash2 size={16} />
                    Clear Library
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {songs.slice(0, 10).map((song) => (
                    <div
                      key={song.id}
                      className="flex items-center gap-3 p-2 bg-monster-dark rounded hover:bg-monster-card/50 transition-colors group"
                    >
                      <img
                        src={song.imagePath}
                        alt={song.title}
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{song.title}</p>
                        <p className="text-xs text-gray-400 truncate">{song.artist}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteSong(song.id)}
                        disabled={deletingId === song.id}
                        className="w-8 h-8 flex items-center justify-center rounded bg-monster-crimson/10 hover:bg-monster-crimson text-monster-crimson hover:text-white transition-all disabled:opacity-50 flex-shrink-0"
                        title="Delete song"
                      >
                        {deletingId === song.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} className="flex-shrink-0" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Settings Modal */}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Clear Library Confirmation Modal */}
      {showClearLibraryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-monster-card border border-monster-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-monster-crimson/20 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="text-monster-crimson" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Clear Entire Library?</h3>
                <p className="text-gray-400 text-sm">
                  This will permanently delete all {songs.length} song{songs.length !== 1 ? 's' : ''} from the database and remove all associated media files (MP3, images, and markdown files).
                </p>
                <p className="text-monster-crimson text-sm font-medium mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowClearLibraryModal(false)}
                disabled={clearingLibrary}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleClearLibrary}
                disabled={clearingLibrary}
                className="btn-primary bg-monster-crimson hover:bg-monster-crimson/80"
              >
                {clearingLibrary ? (
                  <>
                    <Loader2 className="inline animate-spin mr-2" size={16} />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="inline mr-2" size={16} />
                    Clear Library
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
